import moment from 'moment-timezone';

export const CalendarDateUtil = {
  /**
   * Sets the global moment timezone. Encapsulated for testability.
   * This is required for react-big-calendar's momentLocalizer to work correctly.
   * @param tz - Timezone string (e.g., 'America/New_York'). Undefined resets to browser default.
   */
  setMomentTimezone(tz?: string) {
    moment.tz.setDefault(tz);
  },

  /**
   * Resets the global moment timezone to the browser default.
   * Should be called on a component unmount to avoid polluting global state.
   */
  resetMomentTimezone() {
    moment.tz.setDefault();
  },

  getTimestampInPast(years = 0, months = 0, days = 0) {
    return moment().subtract({ years, months, days });
  },
  /**
   * Gets the display date for calendar tests.
   * DO NOT USE UNLESS FOR THE TESTS IN THE CALENDAR DIRECTORY
   *
   * This determines which week the calendar should display in tests.
   * Strategy:
   * - Mon-Thu: Show current week (today)
   * - Fri-Sun: Show next week (jump to Monday to avoid weekend rendering issues)
   */
  getDisplayDateForCalendarTests() {
    const starting = moment().set({
      hour: 11,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    /**
     * When adding/subtracting from a moment object it mutates the original.
     * Add a clone method for test convenience.
     */
    starting.clone = () => moment(starting);

    let daysToJumpForward;
    const dayOfWeek = starting.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday

    switch (dayOfWeek) {
      case 0: {
        // Sunday: jump to Monday (1 day)
        daysToJumpForward = 1;
        break;
      }
      case 1:
      case 2:
      case 3:
      case 4: {
        // Monday-Thursday: stay on today (0 days)
        daysToJumpForward = 0;
        break;
      }
      case 5: {
        // Friday: jump to Monday next week (3 days)
        daysToJumpForward = 3;
        break;
      }
      case 6: {
        // Saturday: jump to Monday next week (2 days)
        daysToJumpForward = 2;
        break;
      }
      default: {
        // This should never happen as day() returns 0-6
        daysToJumpForward = 0;
        break;
      }
    }

    return starting.add(daysToJumpForward, 'days');
  },

  /**
   * Gets the event start date for calendar tests.
   * DO NOT USE UNLESS FOR THE TESTS IN THE CALENDAR DIRECTORY
   *
   * This determines when test events should be scheduled.
   * Strategy:
   * - Mon-Thu: Create events for tomorrow (+1 day from today)
   * - Fri-Sun: Create events for next Monday (to avoid weekend/same-day restrictions)
   *
   * @param timezone - Optional timezone string (e.g., 'America/New_York'). Uses system timezone if not provided.
   */
  getEventStartDateForCalendarTests(timezone?: string) {
    const starting = timezone ? moment.tz(timezone) : moment();
    starting.set({
      hour: 11,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    /**
     * When adding/subtracting from a moment object it mutates the original.
     * Add a clone method for test convenience.
     */
    starting.clone = () =>
      timezone ? moment.tz(starting, timezone) : moment(starting);

    let daysToJumpForward;
    const dayOfWeek = starting.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday

    switch (dayOfWeek) {
      case 0: {
        // Sunday: jump to Monday (1 day)
        daysToJumpForward = 1;
        break;
      }
      case 1:
      case 2:
      case 3:
      case 4: {
        // Monday-Thursday: create events for tomorrow (+1 day)
        daysToJumpForward = 1;
        break;
      }
      case 5: {
        // Friday: jump to Monday next week (3 days)
        daysToJumpForward = 3;
        break;
      }
      case 6: {
        // Saturday: jump to Monday next week (2 days)
        daysToJumpForward = 2;
        break;
      }
      default: {
        // This should never happen as day() returns 0-6
        daysToJumpForward = 1;
        break;
      }
    }

    return starting.add(daysToJumpForward, 'days');
  },

  getDateStampsForSetSystemTimeMocking(timezone?: string) {
    return Array.from({ length: 7 })
      .fill(null)
      .flatMap((_, idx) => {
        return [0, 12, 23].map(hour => {
          const baseMoment = timezone ? moment.tz(timezone) : moment();
          return baseMoment
            .set({
              hour,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .add({ days: idx });
        });
      });
  },
};
